import { useQueryPrivate, useMutatePrivate } from "../../hooks/useFetchPrivate";

export const useGetOrganizationsApi = (
  { page, limit, order, orderBy, name } = { page: 0, limit: 5 },
  useQueryOptions = {},
) => {
  const orderParam = order !== undefined ? `sort_order=${order}` : "";
  const orderByParam = orderBy !== undefined ? `sort_field=${orderBy}` : "";
  const nameParam = name ? `name=${name}` : "";
  const limitParam = limit ? `limit=${limit}` : "";
  const pageParam = page !== undefined ? `page=${page}` : "";

  const queryParams = [orderParam, orderByParam, limitParam, pageParam, nameParam]
    .filter((param) => param !== "")
    .join("&");

  return useQueryPrivate(
    { url: `admin/organization${queryParams ? `?${queryParams}` : ""}` },
    {
      queryKey: ["admin/organizations", [page, limit, order, orderBy, name].join(":")],
      keepPreviousData: true,
      staleTime: 5000,
      ...useQueryOptions,
    },
  );
};

export const useGetOrganizationInfo = (organizationId, useQueryOptions = {}) => {
  return useQueryPrivate(
    { url: `admin/organization/${organizationId}` },
    { queryKey: ["admin/organization", organizationId], ...useQueryOptions },
  );
};

export const useGetOrganizationUserApi = (
  { organizationId, userId },
  useQueryOptions = {},
) => {
  return useQueryPrivate(
    { url: `/admin/organization/${organizationId}/user/${userId}` },
    useQueryOptions,
  );
};

export const useCreateOrganizationsApi = ({ useMutateOptions } = {}) => {
  const { mutate, ...rest } = useMutatePrivate(
    {
      method: "post",
      url: "admin/organization",
    },
    useMutateOptions,
  );
  return {
    mutate: (data) => mutate({ data }),
    ...rest,
  };
};

export const useUpdateOrganizationsApi = ({ useMutateOptions } = {}) => {
  const { mutate, ...rest } = useMutatePrivate({ method: "put" }, useMutateOptions);
  return {
    mutate: ({ orgId, data }) => mutate({ url: `admin/organization/${orgId}`, data }),
    ...rest,
  };
};

export const useSuspendUserApi = ({ useMutateOptions } = {}) => {
  const { mutate, ...rest } = useMutatePrivate({ method: "put" }, useMutateOptions);
  return {
    mutate: ({ userId, orgId, suspend }) =>
      mutate({
        url: `/admin/organization/${orgId}/user/${userId}/suspend`,
        data: { suspend },
      }),
    ...rest,
  };
};

export const useUploadGalleryApi = ({ useMutateOptions } = {}) => {
  const { mutateAsync, ...rest } = useMutatePrivate(
    { method: "post" },
    useMutateOptions,
  );
  return {
    mutateAsync: ({ organizationId, file, name }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", name);
      return mutateAsync({
        url: `admin/organization/${organizationId}/image`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    ...rest,
  };
};

// GET admin/organization/:orgId/user/:userId/gallery?limit=&page=&image_url=
// (replaces the old user/:userId/organization/:orgId/gallery endpoint)
export const useGetUserGalleryApi = (
  { organizationId, userId, page = 1, limit = 10, search = "" },
  useQueryOptions = {},
) => {
  const params = new URLSearchParams({ limit, page });
  if (search) params.set("image_url", search);
  return useQueryPrivate(
    { url: `admin/organization/${organizationId}/user/${userId}/gallery?${params.toString()}` },
    {
      queryKey: ["admin/organization/user/gallery", organizationId, userId, page, limit, search],
      keepPreviousData: true,
      ...useQueryOptions,
    },
  );
};
